import axios from "axios";

// ✅ Use environment variable for API URL (fallback to Railway)
// Note: REACT_APP_API_URL should be the base URL without /api (e.g., https://your-app.up.railway.app)
const API_URL = process.env.REACT_APP_API_URL || "https://myexamnationcms-production.up.railway.app";

const strapi = axios.create({
    baseURL: `${API_URL}/api`,
    headers: { "Content-Type": "application/json" },
});

// ─────────────────────────────────────────────────────────────
// Authentication (Strapi users-permissions)
// ─────────────────────────────────────────────────────────────

export const JWT_STORAGE_KEY = "men_jwt";
export const USER_STORAGE_KEY = "men_user";

// Where manual (pre-payment-gateway) upgrade requests go.
export const SUPPORT_EMAIL = "hello@myexamnation.com";

export interface AuthUser {
    id: number;
    documentId?: string;
    username: string;
    email: string;
    provider?: string;
    confirmed?: boolean;
    plan?: "free" | "pro";
    planExpiresAt?: string | null;
    createdAt?: string;
}

// Mirrors the server-side check in myexamnationCMS/src/utils/subscription.ts.
// Display logic only — actual content gating happens on the server.
export function isProUser(user: AuthUser | null | undefined): boolean {
    if (!user) return false;
    if (user.plan !== "pro") return false;
    if (user.planExpiresAt && new Date(user.planExpiresAt).getTime() < Date.now()) {
        return false;
    }
    return true;
}

export function getStoredJwt(): string | null {
    try {
        return localStorage.getItem(JWT_STORAGE_KEY);
    } catch {
        return null;
    }
}

export interface AuthResponse {
    jwt: string;
    user: AuthUser;
}

// Email sign-up. Username is set to the email — usernames aren't shown
// anywhere in the product and this guarantees uniqueness.
export async function registerWithEmail(email: string, password: string): Promise<AuthResponse> {
    const res = await strapi.post("/auth/local/register", {
        username: email,
        email,
        password,
    });
    return res.data;
}

export async function loginWithEmail(identifier: string, password: string): Promise<AuthResponse> {
    const res = await strapi.post("/auth/local", { identifier, password });
    return res.data;
}

export async function fetchMe(jwt: string): Promise<AuthUser> {
    const res = await strapi.get("/users/me", {
        headers: { Authorization: `Bearer ${jwt}` },
    });
    return res.data;
}

export async function requestPasswordReset(email: string): Promise<void> {
    await strapi.post("/auth/forgot-password", { email });
}

export async function resetPassword(
    code: string,
    password: string,
    passwordConfirmation: string
): Promise<AuthResponse> {
    const res = await strapi.post("/auth/reset-password", {
        code,
        password,
        passwordConfirmation,
    });
    return res.data;
}

// Exchanges the provider access token (returned by Strapi as query params
// on the frontend redirect) for a Strapi JWT + user.
export async function exchangeProviderToken(provider: string, search: string): Promise<AuthResponse> {
    const res = await strapi.get(`/auth/${provider}/callback${search}`);
    return res.data;
}

// Entry point for the OAuth flow — send the browser here.
export function providerConnectUrl(provider: "google" | "facebook"): string {
    return `${API_URL}/api/connect/${provider}`;
}

// ─────────────────────────────────────────────────────────────
// Progress tracking (attempts)
// ─────────────────────────────────────────────────────────────

export interface AttemptAnswer {
    question: string | number; // question documentId
    order: number;
    topic: string | null;
    selected: number | null; // null = skipped/unanswered
    correct: boolean;
}

export interface AttemptPayload {
    quiz: string | number; // quiz documentId
    score: number;
    total: number;
    skipped: number;
    percentage: number;
    answers: AttemptAnswer[];
}

export interface Attempt {
    id: number;
    documentId?: string;
    score: number;
    total: number;
    skipped: number;
    percentage: number;
    answers: AttemptAnswer[] | null;
    completedAt: string;
    quiz: { documentId?: string; title: string; slug?: string } | null;
}

// Saves a completed quiz run. The server forces the user relation and
// timestamp from the JWT — callers can't write anyone else's history.
export async function saveAttempt(payload: AttemptPayload): Promise<void> {
    const jwt = getStoredJwt();
    if (!jwt) return;
    await strapi.post(
        "/attempts",
        { data: payload },
        { headers: { Authorization: `Bearer ${jwt}` } }
    );
}

export async function fetchMyAttempts(): Promise<Attempt[]> {
    const jwt = getStoredJwt();
    if (!jwt) return [];

    const res = await strapi.get("/attempts", {
        params: {
            "populate[quiz][fields][0]": "title",
            "populate[quiz][fields][1]": "slug",
            "sort[0]": "completedAt:desc",
            "pagination[pageSize]": 200,
        },
        headers: { Authorization: `Bearer ${jwt}` },
    });

    return (res.data.data || []).map((a: any) => {
        const attrs = a.attributes || a;
        const quiz = attrs.quiz || a.quiz || null;
        return {
            id: a.id,
            documentId: a.documentId,
            score: attrs.score ?? 0,
            total: attrs.total ?? 0,
            skipped: attrs.skipped ?? 0,
            percentage: attrs.percentage ?? 0,
            answers: Array.isArray(attrs.answers) ? attrs.answers : null,
            completedAt: attrs.completedAt || attrs.createdAt || "",
            quiz: quiz
                ? {
                      documentId: quiz.documentId,
                      title: (quiz.attributes || quiz).title || "Unknown module",
                      slug: (quiz.attributes || quiz).slug,
                  }
                : null,
        };
    });
}

// Strapi v5 error shape: { error: { status, name, message } }
export function getApiErrorMessage(err: any, fallback: string): string {
    const msg = err?.response?.data?.error?.message;
    return typeof msg === "string" && msg.length > 0 ? msg : fallback;
}

// ✅ Fetch all quizzes (metadata only — questions come from /questions,
// where subscription gating is enforced)
export const fetchQuizzes = async () => {
    const res = await strapi.get("/quizzes");
    return res.data.data.map((q: any) => {
        // Handle Strapi v5 response structure (attributes or direct)
        const attributes = q.attributes || q;
        return {
            id: q.id,
            slug: attributes.slug || q.slug || null,
            title: attributes.title || q.title,
            description: attributes.description || q.description,
            documentId: q.documentId || q.id,
        };
    });
};

// ✅ Fetch a quiz by documentId
export async function fetchQuizByDocumentId(documentId: string) {
    const res = await strapi.get(`/quizzes/${documentId}`);
    return res.data.data;
}

// ✅ Fetch quiz by slug
export async function fetchQuizBySlug(slug: string) {
    const res = await strapi.get("/quizzes", {
        params: {
            "filters[slug][$eq]": slug,
        },
    });

    if (!res.data.data || res.data.data.length === 0) {
        return null;
    }

    const quizData = res.data.data[0];
    // Handle Strapi v5 response structure
    const attributes = quizData.attributes || quizData;

    return {
        id: quizData.id,
        documentId: quizData.documentId || quizData.id,
        slug: attributes.slug || quizData.slug,
        title: attributes.title || quizData.title,
        description: attributes.description || quizData.description,
    };
}

function mapQuestion(q: any) {
    const attrs = q.attributes || q;
    const options = attrs.options || q.options || [];

    return {
        id: q.id,
        documentId: q.documentId || q.id,
        text: attrs.text || q.text || "",
        correctOption: attrs.correctOption ?? q.correctOption ?? 0,
        order: attrs.order ?? q.order ?? 0,
        difficulty: attrs.difficulty || q.difficulty || "medium",
        points: attrs.points || q.points || 1,
        explanation: attrs.explanation || q.explanation || "",
        topic: attrs.topic || q.topic || null,
        options: options.map((opt: any) => {
            const optAttrs = opt.attributes || opt;
            return {
                id: opt.id,
                text: optAttrs.text || opt.text || "",
            };
        }),
    };
}

export interface QuestionsResult {
    questions: any[];
    total: number; // total questions in the quiz (incl. locked ones)
    gated: boolean; // true when the server withheld questions
    freeQuestionLimit: number | null;
}

// ✅ Fetch all questions for a quiz (sorted by order).
// Sends the JWT when present so Pro users get the full set; the server
// decides how many questions come back (subscription gating).
export async function fetchQuestionsForQuiz(documentId: string | number): Promise<QuestionsResult> {
    const params = {
        "filters[quiz][documentId][$eq]": documentId,
        "pagination[limit]": 1000,
        "sort[0]": "order:asc",
        "populate": "options",
    };

    const jwt = getStoredJwt();

    let res;
    try {
        res = await strapi.get("/questions", {
            params,
            headers: jwt ? { Authorization: `Bearer ${jwt}` } : undefined,
        });
    } catch (err: any) {
        // An authenticated failure must never break the quiz page — retry
        // anonymously so at least the free tier loads. Covers expired
        // tokens (401) and a misconfigured Authenticated role (403/500):
        // see SUBSCRIPTIONS_SETUP.md → role permissions.
        if (jwt) {
            console.warn(
                "Authenticated questions fetch failed — retrying anonymously.",
                err?.response?.status
            );
            res = await strapi.get("/questions", { params });
        } else {
            throw err;
        }
    }

    const rows: any[] = res.data.data || [];
    const meta = res.data.meta || {};
    const questions = rows.map(mapQuestion);

    return {
        questions,
        total: meta.pagination?.total ?? questions.length,
        gated: !!meta.gated,
        freeQuestionLimit: meta.freeQuestionLimit ?? null,
    };
}
export default strapi;
